<script setup lang="ts">
import {
    Button,
    Input,
    Label,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Textarea,
    ZhConfirmButton,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import {
    api,
    type ExpressImportResult,
    type ExpressToolEntry,
    type StaffExpressTool,
    type StaffExpressToolInput,
} from '@/api'
import CopyButton from '@/components/CopyButton.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import McpPermissionsPicker from '@/components/McpPermissionsPicker.vue'
import OAuthAuthorizeParams from '@/components/OAuthAuthorizeParams.vue'
import ToolImageInput from '@/components/ToolImageInput.vue'
import { useListState } from '@/composables/useListState'
import { expressDocsPage, expressToolPage } from '@/lib/expressDirectory'
import { entriesOfFile, importAll, repeatedSlugs } from '@/lib/expressImport'
import {
    mustGateFromText,
    mustGateText,
    propsPresetFromText,
    propsPresetText,
    slotActionsFromText,
    slotActionsText,
} from '@/lib/expressTemplate'
import { authorizeParamsFromText } from '@/lib/oauthAuthorizeParams'

/**
 * The Staff area's Express tools: the platform's list of systems an enterprise connects by signing in. A platform
 * admin sets each one up once here, and every enterprise is then offered it. A tool signs in on the platform's own
 * sign-in app, or, where its server registers clients itself, with no app at all.
 * Only a platform admin is offered this page, and the API refuses everybody else whatever the portal drew.
 */
const tools = ref<StaffExpressTool[]>([])
const redirectUri = ref('')
const loading = ref(true)
const error = ref<string | null>(null)
const busy = ref(false)
/** The tool being changed, `'new'` while adding one, or null with the form closed. */
const editing = ref<string | null>(null)

const slug = ref('')
const name = ref('')
const description = ref('')
const imageUrl = ref('')
const mcpUrl = ref('')
const scopes = ref('')
const params = ref('')
/** Whether the tool's server registers a client for each connection, so the tool needs no sign-in app. */
const registers = ref(false)
const clientId = ref('')
const clientSecret = ref('')
const docsUrl = ref('')
const category = ref('')
const rank = ref('0')
const keywords = ref('')
const pageCanDo = ref('')
const pageNote = ref('')
const exampleTask = ref('')
/**
 * Its binding template (#587): toolsets, the must-gate list one entry a line, the git preset as name=value lines, and
 * the action map as action=tool lines (#956).
 */
const toolsets = ref('')
const mustGate = ref('')
const propsPreset = ref('')
const slotActions = ref('')
const live = ref(false)

const adding = computed(() => editing.value === 'new')
/** The tool's own page on the public site, worked out from its tool type: what a blank Docs page means. */
const ownPage = computed(() => (slug.value.trim() ? expressToolPage(slug.value) : ''))
/** Bound, not written in the template: see the vue-tsc 3.3.14 `//` note on `LINKS_EXAMPLE` in ExecutionsView.vue. */
const DOCS_URL_PLACEHOLDER = 'https://'
const current = computed(() => tools.value.find(tool => tool.id === editing.value) ?? null)

/** What the list is narrowed to: words typed, and only the tools still waiting for their sign-in app. */
const search = ref('')
const needsClientOnly = ref(false)
/** #633: the platform's list, the same in every enterprise, so its choices are the person's. */
const list = useListState(
    'staff-express-tools',
    { search: { ref: search }, needsClientOnly: { ref: needsClientOnly } },
    { scope: 'person' },
)
const needsClient = (tool: StaffExpressTool): boolean => tool.signIn === 'platform_client' && !tool.clientId
const listed = computed(() => {
    const words = search.value.toLowerCase().split(/\s+/).filter(Boolean)
    return tools.value.filter(tool => {
        const haystack = [tool.name, tool.slug, tool.category, tool.mcpUrl].join(' ').toLowerCase()
        return (!needsClientOnly.value || needsClient(tool)) && words.every(word => haystack.includes(word))
    })
})

/** A list read from a file, waiting to be imported, with what importing it would do. */
const pending = ref<{ entries: ExpressToolEntry[]; plan: ExpressImportResult; untouched: number } | null>(null)
const publish = ref(false)
const notice = ref<string | null>(null)
/** The entries worth a row: what would be added, changed or refused. A tool that stays as it is is only counted. */
const planRows = computed(() => pending.value?.plan.entries.filter(entry => entry.action !== 'unchanged') ?? [])

/** What the API said was wrong, in words. A reason with no copy here names the field it is about, or itself. */
const REFUSALS: ReadonlyMap<string, string> = new Map([
    ['express_tool_slug_taken', 'Another Express tool already uses that tool type.'],
    ['express_tool_slug_fixed', 'The tool type cannot change once the tool is made: connections and tasks name it.'],
    ['express_tool_in_use', 'Enterprises are connected through this tool. Take it off Live instead of deleting it.'],
    [
        'invalid_tool_type',
        'A tool type starts with a letter and uses only lower-case letters, digits, dashes and underscores.',
    ],
    ['express_tool_name_invalid', 'Give the tool a name of up to 120 characters.'],
    ['express_tool_description_invalid', 'Say what a member can do with it, in up to 500 characters.'],
    ['express_tool_mcp_url_invalid', 'Enter the MCP server’s full address, starting with https://.'],
    ['express_tool_client_id_invalid', 'Enter the client ID of the sign-in app registered for this tool.'],
    [
        'express_tool_client_required',
        'A tool that signs in on our app needs its client ID before it can be Live. Save it as a draft until then.',
    ],
    ['express_tool_oauth_scopes_invalid', 'Each permission is one name with no spaces in it. Check what was typed.'],
    [
        'express_tool_authorize_params_invalid',
        'Write each extra sign-in parameter as name=value on its own line. Parameters the OS sets itself, such as client_id, state, scope and redirect_uri, cannot be added.',
    ],
    ['oauth_authorize_params_invalid', 'Use unique name=value lines for additional sign-in parameters.'],
    ['express_tool_docs_url_invalid', 'Enter the full address of the tool’s docs page, or leave it blank.'],
    ['express_tool_category_invalid', 'A category is up to 40 characters.'],
    ['express_tool_rank_invalid', 'The rank is a whole number from 0 to 1000. Above 0 is featured.'],
    ['express_tool_keywords_invalid', 'Keywords are up to 300 characters.'],
    ['express_tool_page_can_do_invalid', 'The page text is up to 4,000 characters.'],
    ['express_tool_page_note_invalid', 'The note is up to 4,000 characters.'],
    ['express_tool_example_task_invalid', 'The example task is up to 1,000 characters.'],
    ['express_tool_toolsets_invalid', 'Toolsets are names separated by commas, with no spaces inside a name.'],
    [
        'express_tool_must_gate_invalid',
        'Each must-gate line is one exact tool name, such as vendor.tool_name, or a {"tool": …, "match": …} entry. No wildcards.',
    ],
    [
        'express_tool_props_preset_invalid',
        'Write each git setting as name=value on its own line, with names starting git. and never git.repo or git.repos.',
    ],
    [
        'express_tool_slot_actions_invalid',
        'Write each action as action=tool on its own line: the action in lowercase words joined by dots (pr.merge), the tool as this server names it with an optional #method=… pin, never with a prefix, and each action once.',
    ],
    ['express_tool_sign_in_invalid', 'The file names a way of signing in the OS does not have.'],
    ['express_tool_import_invalid', 'That file holds no list of tools.'],
    [
        'express_import_file_invalid',
        'That file holds no list of tools. Choose a file exported here, or built for import.',
    ],
    [
        'express_tool_import_field_refused',
        'It carries a client, a token endpoint or Live. Those are set here, never imported.',
    ],
    ['express_tool_import_slug_repeated', 'Its tool type is in the file twice.'],
    [
        'express_tool_import_sign_in_fixed',
        'It would change how an existing tool signs in. Change that here, on the tool.',
    ],
    ['tool_image_invalid', 'The image must be a PNG, JPEG, WebP or GIF.'],
    ['platform_admin_only', 'Only a platform admin can change Express tools.'],
])

function refusal(failure: unknown, fallback: string): string {
    return failure instanceof Error ? (REFUSALS.get(failure.message) ?? fallback) : fallback
}

/** One line on what an import would do to a tool: why it is refused, or which fields change. */
function planDetail(entry: ExpressImportResult['entries'][number]): string {
    if (entry.action === 'refused') {
        return REFUSALS.get(entry.error ?? '') ?? entry.error ?? ''
    }
    return (entry.changes ?? []).map(change => change.field).join(', ')
}

function open(tool: StaffExpressTool | null): void {
    error.value = null
    editing.value = tool?.id ?? 'new'
    slug.value = tool?.slug ?? ''
    name.value = tool?.name ?? ''
    description.value = tool?.description ?? ''
    imageUrl.value = tool?.imageUrl ?? ''
    mcpUrl.value = tool?.mcpUrl ?? ''
    scopes.value = (tool?.oauthScopes ?? []).join(' ')
    params.value = Object.entries(tool?.authorizeParams ?? {})
        .map(([key, value]) => `${key}=${value}`)
        .join('\n')
    registers.value = tool?.signIn === 'registered_client'
    clientId.value = tool?.clientId ?? ''
    clientSecret.value = ''
    docsUrl.value = tool?.docsUrl ?? ''
    live.value = tool?.live ?? false
    fillPage(tool)
    fillTemplate(tool)
}

/** The binding template a new binding of the tool type starts with, into the form. */
function fillTemplate(tool: StaffExpressTool | null): void {
    toolsets.value = tool?.toolsets ?? ''
    mustGate.value = mustGateText(tool?.mustGate)
    propsPreset.value = propsPresetText(tool?.propsPreset)
    slotActions.value = slotActionsText(tool?.slotActions)
}

/** Where the tool sits in the directory and what its public page says, into the form. */
function fillPage(tool: StaffExpressTool | null): void {
    category.value = tool?.category ?? ''
    rank.value = String(tool?.rank ?? 0)
    keywords.value = tool?.keywords ?? ''
    pageCanDo.value = tool?.pageCanDo ?? ''
    pageNote.value = tool?.pageNote ?? ''
    exampleTask.value = tool?.exampleTask ?? ''
}

async function load(): Promise<void> {
    try {
        tools.value = await api.staffExpressTools()
    } catch (failure) {
        error.value = refusal(failure, 'Could not load the Express tools.')
    } finally {
        loading.value = false
    }
}

async function save(): Promise<void> {
    busy.value = true
    error.value = null
    try {
        const body: StaffExpressToolInput = {
            name: name.value,
            description: description.value,
            imageUrl: imageUrl.value,
            mcpUrl: mcpUrl.value,
            oauthScopes: scopes.value.split(/\s+/).filter(Boolean),
            authorizeParams: authorizeParamsFromText(params.value),
            signIn: registers.value ? 'registered_client' : 'platform_client',
            // A tool whose server registers clients holds none: whatever was typed before the switch is not sent.
            clientId: registers.value ? '' : clientId.value,
            docsUrl: docsUrl.value,
            category: category.value,
            rank: Number(rank.value),
            keywords: keywords.value,
            pageCanDo: pageCanDo.value,
            pageNote: pageNote.value,
            exampleTask: exampleTask.value,
            toolsets: toolsets.value,
            mustGate: mustGateFromText(mustGate.value),
            propsPreset: propsPresetFromText(propsPreset.value),
            slotActions: slotActionsFromText(slotActions.value),
            live: live.value,
            // Blank keeps the secret already saved, so an edit never has to retype it.
            ...(clientSecret.value && !registers.value ? { clientSecret: clientSecret.value } : {}),
        }
        if (adding.value) {
            await api.createStaffExpressTool({ ...body, slug: slug.value })
        } else if (editing.value) {
            await api.updateStaffExpressTool(editing.value, body)
        }
        editing.value = null
        await load()
    } catch (failure) {
        error.value = refusal(failure, 'Could not save the Express tool.')
    } finally {
        busy.value = false
    }
}

async function remove(tool: StaffExpressTool): Promise<void> {
    busy.value = true
    error.value = null
    try {
        await api.deleteStaffExpressTool(tool.id)
        await load()
    } catch (failure) {
        error.value = refusal(failure, `Could not delete ${tool.name}.`)
    } finally {
        busy.value = false
    }
}

/** Switches a tool on or off Live from its row. A refusal leaves it as it was and says why. */
async function setLive(tool: StaffExpressTool, next: boolean): Promise<void> {
    busy.value = true
    error.value = null
    try {
        await api.updateStaffExpressTool(tool.id, { live: next })
    } catch (failure) {
        error.value = refusal(failure, `Could not change ${tool.name}.`)
    } finally {
        await load()
        busy.value = false
    }
}

/** Reads a chosen file and asks what importing it would do. Nothing is written until Apply. */
async function chooseFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file) {
        return
    }
    busy.value = true
    error.value = null
    notice.value = null
    pending.value = null
    try {
        const entries = entriesOfFile(await file.text())
        const repeated = repeatedSlugs(entries)
        if (repeated.length > 0) {
            error.value = `These tool types are in the file more than once: ${repeated.join(', ')}.`
            return
        }
        const plan = await importAll(api.importStaffExpressTools, entries, { dryRun: true })
        const named = new Set(entries.map(entry => String(entry.slug).toLowerCase()))
        pending.value = { entries, plan, untouched: tools.value.filter(tool => !named.has(tool.slug)).length }
        publish.value = false
    } catch (failure) {
        error.value = refusal(failure, 'Could not read that file.')
    } finally {
        busy.value = false
    }
}

async function applyImport(): Promise<void> {
    const waiting = pending.value
    if (!waiting) {
        return
    }
    busy.value = true
    error.value = null
    try {
        const done = await importAll(api.importStaffExpressTools, waiting.entries, {
            dryRun: false,
            publish: publish.value,
        })
        pending.value = null
        notice.value = done.applied
            ? `Imported: ${done.created} added, ${done.updated} changed, ${done.unchanged} already up to date.`
            : 'Part of the list was refused, so that part was not imported. Choose the file again to see why.'
    } catch (failure) {
        error.value = refusal(failure, 'The import stopped part way. Choose the file again: what was imported stays.')
    } finally {
        await load()
        busy.value = false
    }
}

/** Saves the whole list as a file: what Import reads, with no client in it. */
async function exportList(): Promise<void> {
    busy.value = true
    error.value = null
    try {
        const file = new Blob([`${JSON.stringify(await api.exportStaffExpressTools(), null, 4)}\n`], {
            type: 'application/json',
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(file)
        link.download = 'express-tools.json'
        link.click()
        URL.revokeObjectURL(link.href)
    } catch (failure) {
        error.value = refusal(failure, 'Could not export the list.')
    } finally {
        busy.value = false
    }
}

onMounted(async () => {
    await load()
    try {
        redirectUri.value = (await api.oauthRedirectUri()).redirectUri
    } catch {
        redirectUri.value = ''
    }
})
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle kicker="Staff">Express tools</ZhPageTitle>
		<p class="text-muted-foreground max-w-3xl text-sm">
			Systems every enterprise can connect by pressing Connect and signing in. Each one is set up once here: on a
			sign-in app registered by the platform, or with no app where the tool’s server registers clients itself. A
			tool that is not Live is a draft: only a platform admin sees it on Tools, to try it before release.
		</p>
		<ZhPanel v-if="redirectUri" class="space-y-1 text-sm" data-testid="staff-redirect-uri">
			<p class="text-muted-foreground">Register this redirect address on each tool’s sign-in app:</p>
			<p class="flex flex-wrap items-center gap-2">
				<code class="break-all">{{ redirectUri }}</code>
				<CopyButton :value="redirectUri" label="Copy redirect address" />
			</p>
		</ZhPanel>
		<p v-if="error" class="text-destructive text-sm" role="alert" data-testid="staff-express-error">{{ error }}</p>
		<p v-if="notice" class="text-sm" role="status" data-testid="staff-express-notice">{{ notice }}</p>

		<div class="flex flex-wrap items-center gap-2">
			<ListSearchField
				v-model="search"
				type="search"
				class="w-full sm:w-64"
				placeholder="Search tools"
				aria-label="Search Express tools"
				data-testid="staff-express-search"
			/>
			<div class="flex items-center gap-2">
				<Switch id="express-needs-client-only" v-model="needsClientOnly" />
				<Label for="express-needs-client-only">Needs a client</Label>
			</div>
			<Button v-if="!list.isDefault.value" type="button" variant="outline" size="sm" @click="list.clear()">Clear filters</Button>
			<span class="grow" />
			<input
				id="express-import-file"
				type="file"
				accept="application/json,.json"
				class="sr-only"
				data-testid="staff-express-import-file"
				@change="chooseFile"
			/>
			<Button as-child type="button" variant="outline" size="sm" :disabled="busy">
				<label for="express-import-file">Import a list</label>
			</Button>
			<Button
				type="button"
				variant="outline"
				size="sm"
				:disabled="busy || !tools.length"
				data-testid="staff-express-export"
				@click="exportList"
			>
				Export the list
			</Button>
		</div>

		<ZhPanel v-if="pending" class="max-w-3xl space-y-3" data-testid="staff-express-import">
			<ZhSectionHeading as="h2">Import {{ pending.entries.length }} tools</ZhSectionHeading>
			<p class="text-sm" data-testid="staff-express-import-summary">
				{{ pending.plan.created }} to add, {{ pending.plan.updated }} to change,
				{{ pending.plan.unchanged }} already up to date, {{ pending.plan.refused }} refused.
			</p>
			<p class="text-muted-foreground text-sm">
				A new tool is added as a draft. An existing one keeps its client, its secret and whether it is Live.
				<template v-if="pending.untouched">
					{{ pending.untouched }} tools here are not in the file and stay as they are.
				</template>
			</p>
			<Table v-if="planRows.length">
				<TableHeader>
					<TableRow>
						<TableHead>Tool type</TableHead>
						<TableHead>What happens</TableHead>
						<TableHead>Detail</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<TableRow
						v-for="entry in planRows"
						:key="`${entry.slug}-${entry.action}`"
						data-testid="staff-express-import-entry"
						:data-action="entry.action"
					>
						<TableCell><code>{{ entry.slug }}</code></TableCell>
						<TableCell>{{ { create: 'Added', update: 'Changed', refused: 'Refused', unchanged: '' }[entry.action] }}</TableCell>
						<TableCell>{{ planDetail(entry) }}</TableCell>
					</TableRow>
				</TableBody>
			</Table>
			<div v-if="pending.plan.created" class="flex items-center gap-3">
				<Switch id="express-import-publish" v-model="publish" />
				<Label for="express-import-publish">Make the new tools that need no sign-in app Live now</Label>
			</div>
			<p v-if="pending.plan.refused" class="text-destructive text-sm">
				Nothing is imported while an entry is refused. Correct the file and choose it again.
			</p>
			<div class="flex gap-2">
				<Button
					type="button"
					:disabled="busy || pending.plan.refused > 0 || pending.plan.created + pending.plan.updated === 0"
					data-testid="staff-express-import-apply"
					@click="applyImport"
				>
					Import
				</Button>
				<Button type="button" variant="outline" :disabled="busy" @click="pending = null">Cancel</Button>
			</div>
		</ZhPanel>

		<Table v-if="tools.length">
			<TableHeader>
				<TableRow>
					<TableHead>Tool</TableHead>
					<TableHead>Tool type</TableHead>
					<TableHead>Server</TableHead>
					<TableHead>State</TableHead>
					<TableHead>Connections</TableHead>
					<TableHead><span class="sr-only">Actions</span></TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				<TableRow v-for="tool in listed" :key="tool.id" data-testid="staff-express-tool" :data-slug="tool.slug">
					<TableCell>
						<div class="flex items-center gap-2">
							<img v-if="tool.imageUrl" :src="tool.imageUrl" alt="" class="size-6 object-contain" />
							<span class="font-medium">{{ tool.name }}</span>
							<a
								v-if="tool.live || tool.docsUrl"
								:href="expressDocsPage(tool)"
								target="_blank"
								rel="noopener"
								class="text-muted-foreground text-xs underline underline-offset-4"
								data-testid="staff-express-page"
							>
								Page
							</a>
						</div>
					</TableCell>
					<TableCell><code>{{ tool.slug }}</code></TableCell>
					<TableCell class="max-w-xs truncate">{{ tool.mcpUrl }}</TableCell>
					<TableCell>
						<div class="flex flex-wrap gap-1">
							<ZhStatusPill :tone="tool.live ? 'completed' : 'neutral'" :pulse="false">
								{{ tool.live ? 'Live' : 'Draft' }}
							</ZhStatusPill>
							<Switch
								:model-value="tool.live"
								:disabled="busy"
								:aria-label="`${tool.name} is Live`"
								data-testid="staff-express-live"
								@update:model-value="next => setLive(tool, next === true)"
							/>
							<ZhStatusPill
								v-if="needsClient(tool)"
								tone="action"
								:pulse="false"
								data-testid="staff-express-needs-client"
							>
								Needs a client
							</ZhStatusPill>
						</div>
					</TableCell>
					<TableCell>{{ tool.connections }}</TableCell>
					<TableCell>
						<div class="flex justify-end gap-2">
							<Button type="button" size="sm" variant="outline" :disabled="busy" @click="open(tool)">Edit</Button>
							<ZhConfirmButton
								label="Delete"
								confirm-label="Delete Express tool"
								:title="`Delete ${tool.name}?`"
								description="It leaves the directory for every enterprise. A tool with connections cannot be deleted."
								:disabled="busy"
								@confirm="remove(tool)"
							/>
						</div>
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>
		<ZhEmptyState v-else-if="!loading">No Express tools yet. Add the first one below.</ZhEmptyState>

		<Button v-if="!editing" type="button" data-testid="staff-express-add" @click="open(null)">Add Express tool</Button>

		<ZhPanel v-if="editing" as="form" class="max-w-3xl space-y-4" data-testid="staff-express-form" @submit.prevent="save">
			<ZhSectionHeading as="h2">{{ adding ? 'Add Express tool' : `Edit ${current?.name ?? ''}` }}</ZhSectionHeading>
			<div class="grid gap-4 sm:grid-cols-2">
				<div class="space-y-1">
					<Label for="express-slug">Tool type</Label>
					<Input id="express-slug" v-model="slug" :disabled="!adding" placeholder="lower-case, e.g. mail" required />
					<p class="text-muted-foreground text-xs">The prefix tasks name its tools by. Fixed once made.</p>
				</div>
				<div class="space-y-1">
					<Label for="express-name">Name</Label>
					<Input id="express-name" v-model="name" required />
				</div>
			</div>
			<div class="space-y-1">
				<Label for="express-description">What a member can do with it</Label>
				<Input id="express-description" v-model="description" required />
			</div>
			<ToolImageInput v-model="imageUrl" />
			<div class="space-y-1">
				<Label for="express-mcp-url">MCP server URL</Label>
				<Input id="express-mcp-url" v-model="mcpUrl" type="url" required />
			</div>
			<!--
				The permissions come from the server at the address above, as they do on a connection's own form. When it
				cannot be read, or advertises none, the picker shows a box to type them in.
			-->
			<McpPermissionsPicker
				v-model="scopes"
				:mcp-url="mcpUrl"
				:lookup="api.staffExpressToolPermissions"
				intro="Choose what every connection made through this tool asks for. The list is read from the server at the address above."
				empty-hint="Select the permissions this tool asks for."
			/>
			<div class="space-y-1">
				<OAuthAuthorizeParams id="express-params" v-model="params" />
				<p class="text-muted-foreground text-xs">
					For a sign-in that needs more than the standard parameters, such as one that only gives a long-lived
					sign-in when asked.
				</p>
			</div>
			<div class="space-y-1">
				<div class="flex items-center gap-3">
					<Switch id="express-registers" v-model="registers" />
					<Label for="express-registers">The server registers a client for each connection</Label>
				</div>
				<p class="text-muted-foreground text-xs">
					On for a server that lets any client register itself: the tool needs no sign-in app. Off for one that
					only works with an app registered beforehand, whose client ID and secret go below.
				</p>
			</div>
			<div v-if="!registers" class="grid gap-4 sm:grid-cols-2" data-testid="staff-express-client">
				<div class="space-y-1">
					<Label for="express-client-id">Client ID</Label>
					<Input id="express-client-id" v-model="clientId" autocomplete="off" />
					<p class="text-muted-foreground text-xs">Needed before the tool can be Live.</p>
				</div>
				<div class="space-y-1">
					<Label for="express-client-secret">Client secret</Label>
					<Input id="express-client-secret" v-model="clientSecret" type="password" autocomplete="new-password" />
					<p class="text-muted-foreground text-xs" data-testid="staff-express-secret-note">
						{{
							current?.hasClientSecret
								? 'A secret is saved. Leave this blank to keep it, or type a new one to replace it for every connection.'
								: 'Stored encrypted and never shown again.'
						}}
					</p>
				</div>
			</div>
			<div class="grid gap-4 sm:grid-cols-3">
				<div class="space-y-1">
					<Label for="express-category">Category</Label>
					<Input id="express-category" v-model="category" placeholder="e.g. Email" />
				</div>
				<div class="space-y-1">
					<Label for="express-rank">Rank</Label>
					<Input id="express-rank" v-model="rank" type="number" min="0" max="1000" step="1" />
					<p class="text-muted-foreground text-xs">Higher first. Above 0 is featured.</p>
				</div>
				<div class="space-y-1">
					<Label for="express-keywords">Search keywords</Label>
					<Input id="express-keywords" v-model="keywords" />
				</div>
			</div>
			<div class="space-y-1">
				<Label for="express-page-can-do">Public page: what a member can do</Label>
				<Textarea id="express-page-can-do" v-model="pageCanDo" rows="5" />
				<p class="text-muted-foreground text-xs">
					Markdown. Shown on the tool’s page on the public site, with this logo and description.
				</p>
			</div>
			<div class="space-y-1">
				<Label for="express-page-note">Public page: before you connect</Label>
				<Textarea id="express-page-note" v-model="pageNote" rows="2" />
				<p class="text-muted-foreground text-xs">Optional. Anything a person must do or know first.</p>
			</div>
			<div class="space-y-1">
				<Label for="express-example-task">An example task</Label>
				<Textarea id="express-example-task" v-model="exampleTask" rows="3" />
				<p class="text-muted-foreground text-xs">
					Optional. A request someone could give to make their first task with this tool, in plain words. The
					public page suggests it, and Tools offers it as a new task once the tool is connected.
				</p>
			</div>
			<div class="space-y-4" data-testid="staff-express-template">
				<div class="space-y-1">
					<ZhSectionHeading as="h3">Binding defaults</ZhSectionHeading>
					<p class="text-muted-foreground text-xs">
						What a connection of this tool type starts with when whoever connects it leaves a field out, whether it is
						connected here or set up by hand on Tools. A connection keeps its own copy, so a change here reaches only
						new ones.
					</p>
				</div>
				<div class="space-y-1">
					<Label for="express-toolsets">Toolsets</Label>
					<Input id="express-toolsets" v-model="toolsets" autocomplete="off" placeholder="e.g. issues,repos" />
					<p class="text-muted-foreground text-xs">
						Optional. For a server that serves only the toolsets it is asked for: their names, separated by commas.
					</p>
				</div>
				<div class="space-y-1">
					<Label for="express-must-gate">Tools that must be approved</Label>
					<Textarea id="express-must-gate" v-model="mustGate" rows="3" class="font-mono text-xs" />
					<p class="text-muted-foreground text-xs">
						Optional. One exact tool name a line, such as a merge. A task may use one only with an approval on it.
						Only the CEO can change the list on a connection afterwards.
					</p>
				</div>
				<div class="space-y-1">
					<Label for="express-props-preset">Git settings offered</Label>
					<Textarea id="express-props-preset" v-model="propsPreset" rows="3" class="font-mono text-xs" />
					<p class="text-muted-foreground text-xs">
						Optional. For a code host: the git.* settings Tools offers to add to a connection on this server that
						lacks them, as name=value lines. Never a repository.
					</p>
				</div>
				<div class="space-y-1">
					<Label for="express-slot-actions">Actions mapped to this server’s tools</Label>
					<Textarea
						id="express-slot-actions"
						v-model="slotActions"
						rows="4"
						class="font-mono text-xs"
						placeholder="pr.merge=merge_pull_request&#10;pr.create=create_pull_request"
					/>
					<p class="text-muted-foreground text-xs">
						Optional. For a tool slot: one action=tool a line. The action is what a task names under the slot
						(pr.merge); the tool is this server’s own name for it, with #method=… where one tool does several
						things. A connection made from this entry starts with the map, and a task that names the action runs
						against this server with no change to the task.
					</p>
				</div>
			</div>
			<div class="space-y-1">
				<Label for="express-docs-url">Docs page</Label>
				<Input id="express-docs-url" v-model="docsUrl" type="url" :placeholder="ownPage || DOCS_URL_PLACEHOLDER" />
				<p class="text-muted-foreground text-xs" data-testid="staff-express-own-page">
					Leave blank to use the tool’s own page on the public site, which is built from this form while the
					tool is Live<template v-if="ownPage">:
						<a :href="ownPage" target="_blank" rel="noopener" class="underline underline-offset-4">{{ ownPage }}</a></template
					>.
				</p>
			</div>
			<div class="flex items-center gap-3">
				<Switch id="express-live" v-model="live" />
				<Label for="express-live">Live: offered to every enterprise</Label>
			</div>
			<div class="flex gap-2">
				<Button type="submit" :disabled="busy">{{ adding ? 'Add Express tool' : 'Save' }}</Button>
				<Button type="button" variant="outline" :disabled="busy" @click="editing = null">Cancel</Button>
			</div>
		</ZhPanel>
	</section>
</template>
